<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { TRIGGER_EVENTS } from './flowHelper';
import {
  AUTOMATION_TEMPLATES,
  TEMPLATE_CATEGORIES,
  TEMPLATE_EVENTS,
} from './flowTemplates';

const emit = defineEmits(['close', 'scratch', 'select']);

const { t } = useI18n();
const isRTL = useMapGetter('accounts/isRTL');

const category = ref('all');
const selectedEvents = ref([]);

const events = computed(() =>
  TEMPLATE_EVENTS.map(event => ({
    ...event,
    label: t(TRIGGER_EVENTS.find(({ key }) => key === event.key).labelKey),
  }))
);

const allSelected = computed(
  () => selectedEvents.value.length === events.value.length
);

const toggleAll = () => {
  selectedEvents.value = allSelected.value ? [] : events.value.map(e => e.key);
};

const templates = computed(() =>
  AUTOMATION_TEMPLATES.filter(
    template =>
      (category.value === 'all' || template.category === category.value) &&
      (!selectedEvents.value.length ||
        selectedEvents.value.includes(template.event))
  )
);
</script>

<template>
  <Teleport to="body">
    <div
      :dir="isRTL ? 'rtl' : 'ltr'"
      class="fixed inset-0 z-[10000] flex bg-n-alpha-black1 backdrop-blur-[2px] p-4"
    >
      <div
        class="flex w-full h-full overflow-hidden rounded-xl bg-n-background shadow-xl outline outline-1 outline-n-container"
      >
        <aside
          class="flex flex-col w-80 shrink-0 gap-6 p-6 overflow-y-auto border-r border-n-weak"
        >
          <h2 class="mb-0 text-xl font-medium text-n-slate-12">
            {{ t('AUTOMATION.TEMPLATES.TITLE') }}
          </h2>
          <Button
            :label="t('AUTOMATION.TEMPLATES.FROM_SCRATCH')"
            class="w-full"
            @click="emit('scratch')"
          />

          <div class="flex flex-col gap-3">
            <h3 class="mb-0 text-sm font-medium text-n-slate-12">
              {{ t('AUTOMATION.TEMPLATES.EVENTS') }}
            </h3>
            <label
              class="flex items-center gap-3 text-sm cursor-pointer text-n-slate-12"
            >
              <input
                type="checkbox"
                class="!mb-0 !size-4"
                :checked="allSelected"
                @change="toggleAll"
              />
              {{ t('AUTOMATION.TEMPLATES.SELECT_ALL') }}
            </label>
            <label
              v-for="event in events"
              :key="event.key"
              class="flex items-center gap-3 text-sm cursor-pointer text-n-slate-12"
            >
              <input
                v-model="selectedEvents"
                type="checkbox"
                class="!mb-0 !size-4"
                :value="event.key"
              />
              <Icon
                :icon="event.icon"
                class="size-4 shrink-0 text-n-slate-11"
              />
              <span class="truncate">{{ event.label }}</span>
            </label>
          </div>
        </aside>

        <section class="flex flex-col flex-1 min-w-0">
          <div
            class="flex items-start justify-between gap-4 px-6 py-5 border-b border-n-weak"
          >
            <div class="flex flex-wrap gap-2">
              <button
                v-for="item in TEMPLATE_CATEGORIES"
                :key="item.key"
                type="button"
                class="px-4 py-2 text-sm rounded-lg border transition-colors"
                :class="
                  category === item.key
                    ? 'border-n-brand bg-n-brand/10 text-n-brand'
                    : 'border-transparent bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
                "
                @click="category = item.key"
              >
                {{ t(item.labelKey) }}
              </button>
            </div>
            <Button
              icon="i-lucide-x"
              color="slate"
              variant="ghost"
              size="sm"
              @click="emit('close')"
            />
          </div>

          <div class="flex-1 p-6 overflow-y-auto">
            <p
              v-if="!templates.length"
              class="py-12 text-sm text-center text-n-slate-11"
            >
              {{ t('AUTOMATION.TEMPLATES.EMPTY') }}
            </p>
            <div
              v-else
              class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
            >
              <button
                v-for="template in templates"
                :key="template.id"
                type="button"
                class="flex flex-col overflow-hidden text-left transition-shadow border rounded-lg border-n-weak bg-n-solid-2 hover:shadow-md"
                @click="emit('select', template)"
              >
                <div
                  class="grid h-36 place-items-center"
                  :class="template.color"
                >
                  <Icon :icon="template.icon" class="size-14" />
                </div>
                <span class="p-5 text-base text-n-slate-12">
                  {{ t(template.titleKey) }}
                </span>
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </Teleport>
</template>
